import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { customEventSymbolApi } from '../../api/customEventSymbols';

export const fetchUserAssociations = createAsyncThunk(
  'customEventSymbols/fetchUserAssociations',
  async (activeOnly = false, { rejectWithValue }) => {
    try {
      const response = await customEventSymbolApi.getUserAssociations(activeOnly);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const fetchSymbolsForEvent = createAsyncThunk(
  'customEventSymbols/fetchSymbolsForEvent',
  async (eventId, { rejectWithValue }) => {
    try {
      const response = await customEventSymbolApi.getSymbolsForEvent(eventId);
      return { eventId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const activateEvent = createAsyncThunk(
  'customEventSymbols/activateEvent',
  async ({ eventId, symbol, config }, { rejectWithValue }) => {
    try {
      const response = await customEventSymbolApi.activateEvent(eventId, symbol, config);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deactivateEvent = createAsyncThunk(
  'customEventSymbols/deactivateEvent',
  async ({ eventId, symbol }, { rejectWithValue }) => {
    try {
      const response = await customEventSymbolApi.deactivateEvent(eventId, symbol);
      return { eventId, symbol, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const updateEventConfig = createAsyncThunk(
  'customEventSymbols/updateEventConfig',
  async ({ eventId, symbol, config }, { rejectWithValue }) => {
    try {
      const response = await customEventSymbolApi.updateConfig(eventId, symbol, config);
      return { eventId, symbol, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const deleteAssociation = createAsyncThunk(
  'customEventSymbols/deleteAssociation',
  async ({ eventId, symbol }, { rejectWithValue }) => {
    try {
      const response = await customEventSymbolApi.deleteAssociation(eventId, symbol);
      return { eventId, symbol, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const enableAllForEvent = createAsyncThunk(
  'customEventSymbols/enableAllForEvent',
  async ({ eventId, symbols, defaultConfig }, { rejectWithValue }) => {
    try {
      const response = await customEventSymbolApi.enableAllForEvent(eventId, symbols, defaultConfig);
      return { eventId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

export const disableAllForEvent = createAsyncThunk(
  'customEventSymbols/disableAllForEvent',
  async ({ eventId, symbols }, { rejectWithValue }) => {
    try {
      const response = await customEventSymbolApi.disableAllForEvent(eventId, symbols);
      return { eventId, ...response };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || error.message);
    }
  }
);

const initialState = {
  associations: [],
  associationsLoading: false,
  associationsError: null,

  currentEventSymbols: [],
  currentEventId: null,
  currentEventLoading: false,
  currentEventError: null,

  isActivating: false,
  isDeactivating: false,
  isUpdating: false,
  isDeleting: false,
  isBulkUpdating: false,

  successMessage: null,

  availableSymbols: [
    'AAPL', 'MSFT', 'GOOGL', 'AMZN', 'TSLA', 'META', 'NVDA', 'NFLX',
    'BTC/USD', 'ETH/USD', 'SOL/USD', 'ADA/USD',
    'SPY', 'QQQ', 'IWM', 'GLD', 'SLV'
  ]
};

const customEventSymbolsSlice = createSlice({
  name: 'customEventSymbols',
  initialState,
  reducers: {
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearErrors: (state) => {
      state.associationsError = null;
      state.currentEventError = null;
    },
    clearCurrentEvent: (state) => {
      state.currentEventSymbols = [];
      state.currentEventId = null;
      state.currentEventError = null;
    },
    addAvailableSymbol: (state, action) => {
      const symbol = action.payload.toUpperCase();
      if (!state.availableSymbols.includes(symbol)) {
        state.availableSymbols.push(symbol);
        state.availableSymbols.sort();
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserAssociations.pending, (state) => {
        state.associationsLoading = true;
        state.associationsError = null;
      })
      .addCase(fetchUserAssociations.fulfilled, (state, action) => {
        state.associationsLoading = false;
        state.associations = action.payload.associations || [];
      })
      .addCase(fetchUserAssociations.rejected, (state, action) => {
        state.associationsLoading = false;
        state.associationsError = action.payload;
      })

      .addCase(fetchSymbolsForEvent.pending, (state) => {
        state.currentEventLoading = true;
        state.currentEventError = null;
      })
      .addCase(fetchSymbolsForEvent.fulfilled, (state, action) => {
        state.currentEventLoading = false;
        state.currentEventId = action.payload.eventId;
        state.currentEventSymbols = action.payload.associations || [];
      })
      .addCase(fetchSymbolsForEvent.rejected, (state, action) => {
        state.currentEventLoading = false;
        state.currentEventError = action.payload;
      })

      .addCase(activateEvent.pending, (state) => {
        state.isActivating = true;
      })
      .addCase(activateEvent.fulfilled, (state, action) => {
        state.isActivating = false;
        state.successMessage = 'Event activated successfully';

        const newAssociation = action.payload.association;
        if (!newAssociation) {
          return;
        }
        const existingIndex = state.associations.findIndex(
          assoc => assoc.eventSymbol === newAssociation.eventSymbol
        );

        if (existingIndex >= 0) {
          state.associations[existingIndex] = newAssociation;
        } else {
          state.associations.push(newAssociation);
        }

        if (state.currentEventId === newAssociation.eventId) {
          const currentIndex = state.currentEventSymbols.findIndex(
            assoc => assoc.symbol === newAssociation.symbol
          );

          if (currentIndex >= 0) {
            state.currentEventSymbols[currentIndex] = newAssociation;
          } else {
            state.currentEventSymbols.push(newAssociation);
          }
        }
      })
      .addCase(activateEvent.rejected, (state, action) => {
        state.isActivating = false;
        state.currentEventError = action.payload;
      })

      .addCase(deactivateEvent.pending, (state) => {
        state.isDeactivating = true;
      })
      .addCase(deactivateEvent.fulfilled, (state, action) => {
        state.isDeactivating = false;
        state.successMessage = 'Event deactivated successfully';

        const { eventId, symbol } = action.payload;
        const updatedAssociation = action.payload.association;
        if (!updatedAssociation) {
          return;
        }
        const associationIndex = state.associations.findIndex(
          assoc => assoc.eventId === eventId && assoc.symbol === symbol
        );
        if (associationIndex >= 0) {
          state.associations[associationIndex] = updatedAssociation;
        }

        if (state.currentEventId === eventId) {
          const currentIndex = state.currentEventSymbols.findIndex(
            assoc => assoc.symbol === symbol
          );
          if (currentIndex >= 0) {
            state.currentEventSymbols[currentIndex] = updatedAssociation;
          }
        }
      })
      .addCase(deactivateEvent.rejected, (state, action) => {
        state.isDeactivating = false;
        state.currentEventError = action.payload;
      })

      .addCase(updateEventConfig.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateEventConfig.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.successMessage = 'Event configuration updated successfully';

        const updatedAssociation = action.payload.association;
        if (!updatedAssociation) {
          return;
        }
        const associationIndex = state.associations.findIndex(
          assoc => assoc.eventSymbol === updatedAssociation.eventSymbol
        );
        if (associationIndex >= 0) {
          state.associations[associationIndex] = updatedAssociation;
        }

        if (state.currentEventId === updatedAssociation.eventId) {
          const currentIndex = state.currentEventSymbols.findIndex(
            assoc => assoc.symbol === updatedAssociation.symbol
          );
          if (currentIndex >= 0) {
            state.currentEventSymbols[currentIndex] = updatedAssociation;
          } else {
            state.currentEventSymbols.push(updatedAssociation);
          }
        }
      })
      .addCase(updateEventConfig.rejected, (state, action) => {
        state.isUpdating = false;
        state.currentEventError = action.payload;
      })

      .addCase(deleteAssociation.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteAssociation.fulfilled, (state, action) => {
        state.isDeleting = false;
        state.successMessage = 'Event association deleted';

        const { eventId, symbol } = action.payload;
        const eventSymbol = action.payload.association?.eventSymbol;
        state.associations = state.associations.filter(
          (assoc) => {
            if (eventSymbol) {
              return assoc.eventSymbol !== eventSymbol;
            }
            if (eventId && symbol) {
              return !(assoc.eventId === eventId && assoc.symbol === symbol);
            }
            if (symbol) {
              return assoc.symbol !== symbol;
            }
            return true;
          }
        );

        if (state.currentEventId === eventId) {
          state.currentEventSymbols = state.currentEventSymbols.filter(
            assoc => assoc.symbol !== symbol
          );
        }
      })
      .addCase(deleteAssociation.rejected, (state, action) => {
        state.isDeleting = false;
        state.currentEventError = action.payload;
      })

      .addCase(enableAllForEvent.pending, (state) => {
        state.isBulkUpdating = true;
      })
      .addCase(enableAllForEvent.fulfilled, (state, action) => {
        state.isBulkUpdating = false;
        state.successMessage = 'Event enabled for all selected symbols';

        (action.payload.updated || []).forEach((association) => {
          const index = state.associations.findIndex(
            assoc => assoc.eventSymbol === association.eventSymbol
          );
          if (index >= 0) {
            state.associations[index] = association;
          } else {
            state.associations.push(association);
          }

          if (state.currentEventId === association.eventId) {
            const currentIndex = state.currentEventSymbols.findIndex(
              assoc => assoc.symbol === association.symbol
            );
            if (currentIndex >= 0) {
              state.currentEventSymbols[currentIndex] = association;
            } else {
              state.currentEventSymbols.push(association);
            }
          }
        });
      })
      .addCase(enableAllForEvent.rejected, (state, action) => {
        state.isBulkUpdating = false;
        state.currentEventError = action.payload;
      })

      .addCase(disableAllForEvent.pending, (state) => {
        state.isBulkUpdating = true;
      })
      .addCase(disableAllForEvent.fulfilled, (state, action) => {
        state.isBulkUpdating = false;
        state.successMessage = 'Event disabled for all selected symbols';

        (action.payload.deleted || []).forEach((association) => {
          state.associations = state.associations.filter(
            assoc => assoc.eventSymbol !== association.eventSymbol
          );

          if (state.currentEventId === association.eventId) {
            state.currentEventSymbols = state.currentEventSymbols.filter(
              assoc => assoc.symbol !== association.symbol
            );
          }
        });
      })
      .addCase(disableAllForEvent.rejected, (state, action) => {
        state.isBulkUpdating = false;
        state.currentEventError = action.payload;
      });
  }
});

export const {
  clearSuccessMessage,
  clearErrors,
  clearCurrentEvent,
  addAvailableSymbol
} = customEventSymbolsSlice.actions;

export default customEventSymbolsSlice.reducer;
